import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chiama } from './cliente.js';
import type { TipoAnagrafica } from './anagrafiche.js';

/**
 * Creare un'anagrafica o un articolo a mano.
 *
 * Sono le due cose che si fanno di rado — arrivano quasi sempre da Business
 * Central — e per questo devono funzionare bene la prima volta: chi le usa
 * non le usa abbastanza spesso da sapere dove si inceppano.
 */

export interface NuovaAnagrafica {
  type: TipoAnagrafica;
  name: string;
  firstName?: string | null;
  lastName?: string | null;
  bcCodeSpa?: string | null;
  bcCodeInc?: string | null;
  taxNumber?: string | null;
  taxCardId?: string | null;
  notes?: string | null;
}

export function useCreaAnagrafica() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: NuovaAnagrafica) =>
      chiama<{ id: number }>('/api/identities', { metodo: 'POST', corpo: dati }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['anagrafiche'] });
    },
  });
}

/**
 * Chi si chiama già così.
 *
 * Non blocca niente: mostra chi c'è, e chi sta creando decide. Due anagrafiche
 * con lo stesso nome e sedi diverse esistono davvero.
 */
export function useAnagraficheSimili(nome: string) {
  const pulito = nome.trim();
  return useQuery({
    queryKey: ['anagrafiche-simili', pulito],
    queryFn: () =>
      chiama<{ rows: { id: number; name: string; type: string; bcCodeSpa: string | null }[] }>(
        `/api/identities/similar?name=${encodeURIComponent(pulito)}`,
      ),
    enabled: pulito.length >= 3,
  });
}

export interface NuovoArticolo {
  description: string;
  sku?: string | null;
  bcNumber?: string | null;
  alias?: string | null;
  notes?: string | null;
  lineId?: number | null;
  productId?: number | null;
  typologyId?: number | null;
  formatId?: number | null;
  unitOfMeasurementId?: number | null;
}

export function useCreaArticolo() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: NuovoArticolo) =>
      chiama<{ id: number; senzaNumeroBc: boolean }>('/api/items', {
        metodo: 'POST',
        corpo: dati,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['articoli'] });
    },
  });
}

export interface AttributiArticolo {
  lines: { id: number; label: string }[];
  products: { id: number; label: string }[];
  typologies: { id: number; label: string }[];
  formats: { id: number; label: string }[];
  units: { id: number; label: string }[];
}

export function useAttributiArticolo() {
  return useQuery({
    queryKey: ['attributi-articolo'],
    queryFn: () => chiama<AttributiArticolo>('/api/items/attributes'),
    staleTime: 10 * 60_000,
  });
}
